import * as React from 'react';
import { createStore } from 'redux';
import appStore from './Redux/Reducers';
import ReactModal from 'react-modal';
import WebSocketController from './Utilities/WebSocketController';
import 'handsontable/dist/handsontable.full.css';
import 'purecss/build/pure-min.css';

ReactModal.setAppElement('body');

const webSocketController = new WebSocketController();
const webSocketSettings = (window as any).__webSocketSettings;
if (webSocketSettings) {
    webSocketController.initialize(webSocketSettings.messageHostUri, webSocketSettings.username);
}

const CertificateHolders = React.lazy(() => import('./Modules/PolicyInfo/CertificateHolders/CertificateHolders'));
const EndorsementView = React.lazy(() => import('./Modules/PolicyInfo/Endorsements/EndorsementView'));
const GeneralEndorsementPanel = React.lazy(() => import('./Modules/PolicyInfo/GeneralEndorsements/GeneralEndorsementPanel'));
const VehicleSchedule = React.lazy(() => import('./Components/VehicleSchedule/VehicleSchedule'));
const AdditionalPropertySchedule = React.lazy(() => import('./Components/AdditionalPropertySchedule/AdditionalPropertySchedule'));
const BuildingLocationSchedule = React.lazy(() => import('./Components/BuildingLocationSchedule/BuildingLocationSchedule'));
const AccountingReports = React.lazy(() => import('./Modules/Reports/AccountingReports'));
const PolicyAgencyInfo = React.lazy(() => import('./Modules/PolicyInfo/AgencyInfo/PolicyAgencyInfo'));
const BondCoverage = React.lazy(() => import('./Modules/Bond/BondCoverage'));
const WorkersCompCoverage = React.lazy(() => import('./Modules/WorkersComp/'));
const WorkersCompRosterCoverage = React.lazy(() => import('./Modules/WorkersCompRoster/'));
const AutoLiabilityCoverage = React.lazy(() => import('./Modules/AutoLiability/'));
const AutoPhysicalDamageCoverage = React.lazy(() => import('./Modules/AutoPhysicalDamage'));
const LawEnforcementLiabilityCoverage = React.lazy(() => import('./Modules/LawEnforcementLiability'));
const PublicOfficialLiabilityCoverage = React.lazy(() => import('./Modules/PublicOfficialLiability'));
const GeneralLiabilityCoverage = React.lazy(() => import('./Modules/GeneralLiability/'));
const PropertyCoverage = React.lazy(() => import('./Modules/Property/'));
const ExcessLiabilityCoverage = React.lazy(() => import('./Modules/Excess/'));
const ExcessLiabilityMultipleElectionCoverage = React.lazy(() => import('./Modules/ExcessMultipleElection/'));
const CopyRenewalButton = React.lazy(() => import('./Modules/RenewalInfo/Components/CopyRenewalButton'));
const CopyNewBusinessButton = React.lazy(() => import('./Modules/NewBusiness/Components/CopyNewBusinessButton'));
const ViewRenewalsButton = React.lazy(() => import('./Modules/PolicyInfo/Components/ViewRenewalsButton'));
const PolicyDetails = React.lazy(() => import('./Modules/PolicyInfo/PolicyDetails/PolicyDetails'));
const BatchBindGrid = React.lazy(() => import('./Modules/RenewalInfo/Components/BatchBindGrid'));
const RemoveCoverageWarning = React.lazy(() => import('./Modules/NewBusiness/Components/RemoveCoverageWarning'));
const SubCoverage = React.lazy(() => import('./Components/SubCoverage'));
const PoolDiscounts = React.lazy(() => import('./Modules/PolicyInfo/PoolDiscounts/PoolDiscounts'));
const ResetStore = React.lazy(() => import('./Modules/ReduxHelper'));
const RatePackageEditor = React.lazy(() => import('./Modules/ProgramYearEditor/RatePackageEditor'));
const ProgramYearEditorDetails = React.lazy(() => import('./Modules/ProgramYearEditor/ProgramYearEditorDetails'));
const QuestionnaireSearch = React.lazy(() => import('./Modules/Questionnaire/QuestionnaireSearch'));
const MemberContacts = React.lazy(() => import('./Modules/MemberInfo/MemberContacts/MemberContacts'));
const PolicySearch = React.lazy(() => import('./Modules/Policy/Search'));
const RenewalSearch = React.lazy(() => import('./Modules/Renewal/Search'));
const NewBusinessSearch = React.lazy(() => import('./Modules/NewBusiness/Search'));
const MemberSearch = React.lazy(() => import('./Modules/MemberInfo/Search'));
const DynamicDocuments = React.lazy(() => import('./Modules/Reports/DynamicDocuments'));
const Coverage = React.lazy(() => import('./Modules/Coverage/Coverage'));
const RateCompare = React.lazy(() => import('./Modules/RateCompare/RateCompare'));
const DriverSchedulePage = React.lazy(() => import('./Modules/Mvr/DriverSchedulePage'));
const MvrPage = React.lazy(() => import('./Modules/Mvr/MvrPage'));
const MemberPortalChanges = React.lazy(() => import('./Modules/Renewal/MemberPortalChanges'));
const PrivacyNotice = React.lazy(() => import('./Components/PrivacyNotice'));
const CookieNotice = React.lazy(() => import('./Components/CookieNotice'));
const SecurityNotice = React.lazy(() => import('./Components/SecurityNotice'));
const InactivityTracker = React.lazy(() => import('./Components/InactivityTracker'));
const SignalRStatusIndicator = React.lazy(() => import('./Components/SignalRStatusIndicator/SignalRStatusIndicator'));
const ManageDepartments = React.lazy(() => import('./Modules/Admin/ManageDepartments'));

const ProgramYearEditorSearch = React.lazy(() => import('./Modules/ProgramYearEditor/ProgramYearEditorSearch'));

const store = createStore(appStore, {});

const WaitingForComponent = (Component: any) => {
    return (props: any) => (
        <React.Suspense fallback={<div>Loading...</div>}>
            <Component {...props} store={store} />
        </React.Suspense>
    );
};

(window as any).ReactComponents = {
    ResetStore: WaitingForComponent(ResetStore),
    CertificateHolders: WaitingForComponent(CertificateHolders),
    GeneralEndorsementPanel: WaitingForComponent(GeneralEndorsementPanel),
    VehicleSchedule: WaitingForComponent(VehicleSchedule),
    AdditionalPropertySchedule: WaitingForComponent(AdditionalPropertySchedule),
    BuildingLocationSchedule: WaitingForComponent(BuildingLocationSchedule),
    EndorsementView: WaitingForComponent(EndorsementView),
    AccountingReports: WaitingForComponent(AccountingReports),
    CopyRenewalButton: WaitingForComponent(CopyRenewalButton),
    CopyNewBusinessButton: WaitingForComponent(CopyNewBusinessButton),
    PolicyAgencyInfo: WaitingForComponent(PolicyAgencyInfo),
    BondCoverage: WaitingForComponent(BondCoverage),
    WorkersCompCoverage: WaitingForComponent(WorkersCompCoverage),
    AutoLiabilityCoverage: WaitingForComponent(AutoLiabilityCoverage),
    AutoPhysicalDamageCoverage: WaitingForComponent(AutoPhysicalDamageCoverage),
    LawEnforcementLiabilityCoverage: WaitingForComponent(LawEnforcementLiabilityCoverage),
    PublicOfficialLiabilityCoverage: WaitingForComponent(PublicOfficialLiabilityCoverage),
    GeneralLiabilityCoverage: WaitingForComponent(GeneralLiabilityCoverage),
    PropertyCoverage: WaitingForComponent(PropertyCoverage),
    ViewRenewalsButton: WaitingForComponent(ViewRenewalsButton),
    PolicyDetails: WaitingForComponent(PolicyDetails),
    BatchBindGrid: WaitingForComponent(BatchBindGrid),
    RemoveCoverageWarning: WaitingForComponent(RemoveCoverageWarning),
    PoolDiscounts: WaitingForComponent(PoolDiscounts),
    SubCoverage: WaitingForComponent(SubCoverage),
    ProgramYearEditorSearch: WaitingForComponent(ProgramYearEditorSearch),
    ProgramYearEditorDetails: WaitingForComponent(ProgramYearEditorDetails),
    RatePackageEditor: WaitingForComponent(RatePackageEditor),
    QuestionnaireSearch: WaitingForComponent(QuestionnaireSearch),
    ExcessLiabilityCoverage: WaitingForComponent(ExcessLiabilityCoverage),
    ExcessLiabilityMultipleElectionCoverage: WaitingForComponent(ExcessLiabilityMultipleElectionCoverage),
    MemberContacts: WaitingForComponent(MemberContacts),
    WorkersCompRosterCoverage: WaitingForComponent(WorkersCompRosterCoverage),
    PolicySearch: WaitingForComponent(PolicySearch),
    RenewalSearch: WaitingForComponent(RenewalSearch),
    NewBusinessSearch: WaitingForComponent(NewBusinessSearch),
    MemberSearch: WaitingForComponent(MemberSearch),
    DynamicDocuments: WaitingForComponent(DynamicDocuments),
    Coverage: WaitingForComponent(Coverage),
    RateCompare: WaitingForComponent(RateCompare),
    MvrPage: WaitingForComponent(MvrPage),
    MemberPortalChanges: WaitingForComponent(MemberPortalChanges),
    DriverSchedulePage: WaitingForComponent(DriverSchedulePage),
    PrivacyNotice: WaitingForComponent(PrivacyNotice),
    CookieNotice: WaitingForComponent(CookieNotice),
    SecurityNotice: WaitingForComponent(SecurityNotice),
    InactivityTracker: WaitingForComponent(InactivityTracker),
    SignalRStatusIndicator: WaitingForComponent(SignalRStatusIndicator),
    ManageDepartments: WaitingForComponent(ManageDepartments),
};

// Temp way of accessing TypeScript functions from a javascript file. As
// we convert more and more JS files to TypeScript, this should get smaller
// and eventually removed all together.
import HandsontableUtility from './Utilities/HandsontableUtility';
import NavControllerTs from './Utilities/NavControllerTs';

(window as any).controllers = {
    HandsontableUtility: new HandsontableUtility(),
    NavController_ts: new NavControllerTs(),
};
